import HttpRequest from "../../utilities/httpRequest";
import {
  addPrescriptionAction,
  getMedicineInventoryAction,
  getPrescription,
  getPrescriptions,
  medicineInventoryErrorAction,
  updatePrescriptionAction,
  downloadPrescriptionAction,
} from "./PrescriptionActions";
import {
  hideLoader,
  showLoader,
  showSnackbar,
} from "../../utilities/UtilitiesActions";

const getErrorMessage = (error) => {
  if (error.response?.data?.error) return error.response.data.error;
  if (error.message) return error.message;
  return "Something went wrong";
};

export const addPrescriptionRequest = (data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = "prescription";

    return new HttpRequest(dispatch, url, "post", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(showSnackbar("Prescription created successfully", "success"));
        dispatch(addPrescriptionAction(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        dispatch(showSnackbar(`${error.response.data.error}`, "error"));
        return Promise.reject(error);
      }
    );
  };
};

export const getPrescriptionByCaseId = (caseId, isHideLoader) => {
  return (dispatch) => {
    if (!isHideLoader) dispatch(showLoader());

    const url = `prescriptions?caseId=${caseId}`;
    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getPrescriptions(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

export const getPrescriptionById = (id, isHideLoader) => {
  return (dispatch) => {
    if (!isHideLoader) dispatch(showLoader());

    const url = `prescription/${id}`;
    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(getPrescription(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        return Promise.reject(error);
      }
    );
  };
};

export const updatePrescriptionRequest = (id, data) => {
  return (dispatch) => {
    dispatch(showLoader());
    const url = `prescription/${id}`;

    return new HttpRequest(dispatch, url, "put", data).send().then(
      (res) => {
        dispatch(hideLoader());
        dispatch(showSnackbar("Prescription updated successfully", "success"));
        dispatch(updatePrescriptionAction(res.data.data));
        return Promise.resolve(res.data.data);
      },
      (error) => {
        dispatch(hideLoader());
        dispatch(showSnackbar(`${error.response.data.error}`, "error"));
        return Promise.reject(error);
      }
    );
  };
};

/**
 * The four auto-filled prescription sections (root cause, nutrition, lifestyle,
 * yoga) for a patient, for the doctor to review before submitting. Proxied by
 * our backend, which holds the report-service API key.
 * GET /reports/prescription/draft?phone={phone}
 */
export const getPrescriptionDraftRequest = (phone) => {
  return (dispatch) => {
    if (!phone) return Promise.resolve(null);
    const url = `reports/prescription/draft?phone=${encodeURIComponent(phone)}`;
    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => Promise.resolve(res.data || null),
      (error) => Promise.reject(error)
    );
  };
};

/**
 * Queue the Ayurvedic prescription PDF. Returns { report_id } — the render runs
 * async, so poll getPrescriptionReportStatusRequest until COMPLETED.
 * A section omitted from the payload is auto-filled by the report service; one
 * sent empty is treated as deliberately dropped by the doctor.
 * POST /reports/prescription
 */
export const submitPrescriptionReportRequest = (payload) => {
  return (dispatch) => {
    const url = `reports/prescription`;
    return new HttpRequest(dispatch, url, "post", payload).send().then(
      (res) => Promise.resolve(res.data || null),
      (error) => Promise.reject(error)
    );
  };
};

/**
 * Job progress for a queued prescription: PENDING -> PROCESSING -> COMPLETED,
 * or FAILED with an `error`.
 * GET /reports/{reportId}/status
 */
export const getPrescriptionReportStatusRequest = (reportId) => {
  return (dispatch) => {
    if (!reportId) return Promise.reject(new Error("Missing report id"));
    const url = `reports/${encodeURIComponent(reportId)}/status`;
    return new HttpRequest(dispatch, url, "get").send().then(
      (res) => Promise.resolve(res.data || null),
      (error) => Promise.reject(error)
    );
  };
};

const API_BASE = `medicineInventory`;
export const getMedicineInventoryRequest = () => {
  return (dispatch) => {
    return new HttpRequest(dispatch, API_BASE, "get")
      .send()
      .then((res) => {
        dispatch(hideLoader());
        dispatch(getMedicineInventoryAction(res.data.data));
        return Promise.resolve(res.data.data);
      })
      .catch((error) => {
        console.error("API error:", error);

        dispatch(hideLoader());
        const errMsg = getErrorMessage(error);
        dispatch(medicineInventoryErrorAction(errMsg));
        dispatch(showSnackbar(errMsg, "error"));
        return Promise.reject(error);
      });
  };
};

export const downloadPrescription = (
  userId,
  prescriptionId,
  patientName,
  isHideLoader
) => {
  return (dispatch) => {
    if (!isHideLoader) dispatch(showLoader());

    const url = `patient/view/prescription?user_id=${userId}&prescriptionId=${prescriptionId}`;

    return new HttpRequest(
      dispatch,
      url,
      "get",
      null,
      {},
      {
        responseType: "arraybuffer",
      }
    )
      .send()
      .then(
        (res) => {
          dispatch(hideLoader());
          const blob = new Blob([res.data], { type: "application/pdf" });
          const blobUrl = window.URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = `Prescription_${patientName}_${userId}_${prescriptionId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          dispatch(downloadPrescriptionAction(blobUrl));

          return Promise.resolve(blobUrl);
        },
        (error) => {
          dispatch(hideLoader());

          let errorMsg = error?.message || "Failed to download prescription";

          if (error?.response?.data) {
            try {
              const parsed = JSON.parse(
                new TextDecoder().decode(error.response.data)
              );
              errorMsg = parsed?.error || errorMsg;
            } catch {}
          }
          dispatch(showSnackbar(errorMsg, "error"));
          return Promise.reject(error);
        }
      );
  };
};
